// Utility for rendering and binding the AI feedback modal
// Usage: const modal = new AiFeedbackModal({ ...options }); modal.open();

import generateUUID from '../src/react/helpers/generateUUID';
import { trapFocus, releaseFocusTrap } from './FocusTrap';
import { registerErrorInRollbar } from './case_experience_in_rails';

import smile1 from 'images/smile-rating-1.svg';
import smile2 from 'images/smile-rating-2.svg';
import smile3 from 'images/smile-rating-3.svg';
import smile4 from 'images/smile-rating-4.svg';
import smile5 from 'images/smile-rating-5.svg';

function escapeHtmlAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtmlText(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const getAiFeedbackModalContent = ({
  experienceQuestion,
  responseOptions,
  continueButtonTitle = 'CONTINUE',
  onCancel,
  onContinue,
}) => {
  const uid = generateUUID();
  const commentFieldId = `ai-feedback-comment-${uid}`;
  const ratingFieldName = `ai-feedback-rating-${uid}`;
  const submitHintId = `feedback-submit-hint-${uid}`;

  const html = `
    <div class="ai-feedback-modal-backdrop"></div>
      <div
        class="modal-content gen-modal-panel ai-feedback-modal"
        style="max-width:400px;"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        aria-describedby="feedback-modal-description">
        <input type="hidden" data-ai-feedback-field="rating" value="" />
        <input type="hidden" data-ai-feedback-field="issues" value="" />
        <input type="hidden" data-ai-feedback-field="comment" value="" />
        <h2 id="feedback-modal-title">
          ${experienceQuestion}
        </h2>
        <div id="feedback-modal-description" class="sr-only">
          Provide feedback about your virtual patient chatbot experience.
        </div>
        <fieldset class="ai-feedback-rating">
          <legend>Select a satisfaction rating</legend>
          <div class="rating-options">
            <div class="ai-smile-rating-bar">
              <label class="aq-smile-rating-button">
                <input type="radio" name="${ratingFieldName}" class="rating-radio sr-only" value="5" />
                <img src="${smile5}" alt="Very Satisfied" width="32" height="32" />
              </label>
              <label class="aq-smile-rating-button">
                <input type="radio" name="${ratingFieldName}" class="rating-radio sr-only" value="4" />
                <img src="${smile4}" alt="Satisfied" width="32" height="32" />
              </label>
              <label class="aq-smile-rating-button">
                <input type="radio" name="${ratingFieldName}" class="rating-radio sr-only" value="3" />
                <img src="${smile3}" alt="Neutral" width="32" height="32" />
              </label>
              <label class="aq-smile-rating-button">
                <input type="radio" name="${ratingFieldName}" class="rating-radio sr-only" value="2" />
                <img src="${smile2}" alt="Dissatisfied" width="32" height="32" />
              </label>
              <label class="aq-smile-rating-button">
                <input type="radio" name="${ratingFieldName}" class="rating-radio sr-only" value="1" />
                <img src="${smile1}" alt="Very Dissatisfied" width="32" height="32" />
              </label>
            </div>
          </div>
        </fieldset>
        <fieldset class="ai-feedback-checkboxes">
          <legend>
            Select all that apply (optional)
          </legend>
          ${responseOptions
            .map(
              (option) => `
            <label class="ai-feedback-checkbox-label">
              <input type="checkbox" class="issue-checkbox" value="${escapeHtmlAttr(option)}"> <span>${escapeHtmlText(option)}</span>
            </label>
          `,
            )
            .join('')}
        </fieldset>
        <label for="${commentFieldId}" class="feedback-comment-label">
          How could this experience be improved? (optional)
        </label>
        <textarea id="${commentFieldId}" data-ai-feedback-field="comment-text" rows="4" data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false"></textarea>
        <p class="feedback-support-text">
          If you have an issue that requires immediate assistance, please <a href="https://aquifer.org/knowledge/support/aquifer-support" target="_blank" rel="noopener noreferrer">contact support</a>.
        </p>
        <div class="controls">
          <p id="${submitHintId}" class="feedback-submit-hint sr-only">
            Select a satisfaction rating to continue.
          </p>
          <button
            type="button"
            class="aq-button-2 aq-feedback-submit"
            disabled
            aria-describedby="${submitHintId}">
            ${continueButtonTitle}
          </button>
          <button type="button" class="aq-button-2 outline aq-feedback-cancel">Go Back</button>
        </div>
      </div>
  `;

  function bindEvents(modalRoot) {
    if (!modalRoot) return;
    const cancelBtn = modalRoot.querySelector('.aq-feedback-cancel');
    const continueBtn = modalRoot.querySelector('.aq-feedback-submit');
    const ratingRadios = modalRoot.querySelectorAll('.rating-radio');
    const checkboxes = modalRoot.querySelectorAll('.issue-checkbox');
    const commentBox = modalRoot.querySelector('[data-ai-feedback-field="comment-text"]');
    const hiddenRating = modalRoot.querySelector('[data-ai-feedback-field="rating"]');
    const hiddenIssues = modalRoot.querySelector('[data-ai-feedback-field="issues"]');
    const hiddenComment = modalRoot.querySelector('[data-ai-feedback-field="comment"]');
    const submitHint = modalRoot.querySelector('.feedback-submit-hint');

    // Helper to update hidden fields
    function updateHiddenFields() {
      // Rating
      const selectedRatingRadio = modalRoot.querySelector('.rating-radio:checked');
      if (hiddenRating) {
        hiddenRating.value = selectedRatingRadio ? selectedRatingRadio.value : '';
      }
      // Issues
      const checkedIssues = Array.from(modalRoot.querySelectorAll('.issue-checkbox:checked')).map(
        (cb) => cb.value,
      );
      if (hiddenIssues) {
        hiddenIssues.value = JSON.stringify(checkedIssues);
      }
      // Comment
      if (hiddenComment && commentBox) {
        hiddenComment.value = commentBox.value.trim();
      }
    }

    function updateSubmitButtonState() {
      const selectedRatingRadio = modalRoot.querySelector('.rating-radio:checked');
      if (continueBtn) {
        continueBtn.disabled = !selectedRatingRadio;
      }
      if (submitHint) {
        submitHint.textContent = selectedRatingRadio
          ? 'A satisfaction rating has been selected. You can continue.'
          : 'Select a satisfaction rating to continue.';
      }
    }

    ratingRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        updateHiddenFields();
        updateSubmitButtonState();
      });
    });

    // Checkbox change & keyboard accessibility
    checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        updateHiddenFields();
      });
      cb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          cb.click();
        }
      });
    });

    // Comment box input
    if (commentBox) {
      commentBox.addEventListener('input', () => {
        updateHiddenFields();
      });
    }

    // Cancel button
    if (cancelBtn && typeof onCancel === 'function') {
      cancelBtn.addEventListener('click', onCancel);
    }

    // Submit button
    if (continueBtn && typeof onContinue === 'function') {
      continueBtn.addEventListener('click', () => {
        // Always update hidden fields before submit
        updateHiddenFields();
        const rating = hiddenRating?.value ? parseInt(hiddenRating.value, 10) : null;
        let issues = [];
        try {
          issues = hiddenIssues?.value ? JSON.parse(hiddenIssues.value) : [];
        } catch (e) {
          console.error('Failed to parse hidden issues JSON:', hiddenIssues?.value, e);
          issues = [];
          registerErrorInRollbar('Failed to parse hidden issues JSON', {
            rawValue: hiddenIssues?.value,
            error: e,
          });
        }
        const comment = hiddenComment?.value ?? '';
        onContinue(rating, issues, comment);
      });
    }

    // Initial state
    updateHiddenFields();
    updateSubmitButtonState();
  }

  return { html, bindEvents };
};

export default class AiFeedbackModal {
  constructor({
    experienceQuestion,
    responseOptions,
    continueButtonTitle = 'CONTINUE',
    onCancel,
    onContinue,
    feedbackModalTarget,
    application,
    documentIdValue,
    userIdValue,
    threadIdValue,
  }) {
    this.experienceQuestion = experienceQuestion;
    this.responseOptions = responseOptions;
    this.continueButtonTitle = continueButtonTitle;
    this.onCancel = onCancel;
    this.onContinue = onContinue;
    this.feedbackModalTarget = feedbackModalTarget;
    this.application = application;
    this.documentIdValue = documentIdValue;
    this.userIdValue = userIdValue;
    this.threadIdValue = threadIdValue;
    this.generateUUID = generateUUID;
  }

  getHtmlAndBindEvents() {
    // Use getAiFeedbackModalContent to get html and bindEvents
    const { html, bindEvents } = getAiFeedbackModalContent({
      experienceQuestion: this.experienceQuestion,
      responseOptions: this.responseOptions,
      continueButtonTitle: this.continueButtonTitle,
      onCancel: this.onCancel,
      onContinue: this.onContinue,
    });
    return { html, bindEvents };
  }

  open() {
    if (!this.feedbackModalTarget) return;
    const { html, bindEvents } = this.getHtmlAndBindEvents();
    this.feedbackModalTarget.innerHTML = html;
    this.feedbackModalTarget.style.display = '';
    this.feedbackModalTarget.style.zIndex = '9999';
    document.body.style.overflow = 'hidden';
    bindEvents(this.feedbackModalTarget);
    // Trap focus inside modal
    const modalPanel = this.feedbackModalTarget.querySelector('.ai-feedback-modal');
    if (modalPanel) trapFocus(modalPanel);
    this.bindEscapeKey();
  }

  close() {
    this.unbindEscapeKey();
    if (this.feedbackModalTarget) {
      // Release focus trap
      const modalPanel = this.feedbackModalTarget.querySelector('.ai-feedback-modal');
      if (modalPanel) releaseFocusTrap(modalPanel);
      this.feedbackModalTarget.innerHTML = '';
      this.feedbackModalTarget.style.display = 'none';
    }
    document.body.style.overflow = '';
  }

  bindEscapeKey() {
    this.unbindEscapeKey();
    this._escapeKeyHandler = (event) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      if (typeof this.onCancel === 'function') {
        this.onCancel();
      } else {
        this.close();
      }
    };
    document.addEventListener('keydown', this._escapeKeyHandler, true);
  }

  unbindEscapeKey() {
    if (!this._escapeKeyHandler) return;

    document.removeEventListener('keydown', this._escapeKeyHandler, true);
    this._escapeKeyHandler = null;
  }
}
